import * as React from 'react';
import { ScopeButton } from '@monorepo/shared-lib';
import { Popover, Typography } from 'antd';
import { ReactComponent as IconClose } from 'assets/images/icons/iconClose.svg';
import { ScopeTable } from 'components/Custom/table';
import moment from 'moment';
import { DataLogStatus } from 'store/app/enums';
import { User } from 'store/auth/models';
import { VatReturnLog } from 'store/vat/models';
import styled from 'styled-components';
import { StatusGetter } from 'utils/status-getter';
import { useTranslation } from 'react-i18next';
import { formatDisplayDate, formatDisplayDateRange } from 'i18n/calendarLabels';

const { Text } = Typography;

interface CompareReturnModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (dataLogId: string) => void;
  vatReturnLogs: VatReturnLog[];
  currentDataLogId?: string;
  connectionId: string;
  mappingId: string;
  trigger: React.ReactElement;
  currentUser?: User;
}

const PopoverContent = styled.div`
  max-width: 700px;
  padding: 8px 12px 12px 12px;
`;

const PopoverTitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 10px;
  padding-bottom: 0;
  margin-bottom: 0;
  position: relative;
`;

const PopoverTitleText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-dark, #042a2a);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: var(--color-text-muted, #5a6a6a);
  transition: color 0.2s;
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);

  &:hover {
    color: var(--color-primary, #41aaaf);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const StyledPopover = styled(Popover)`
  .ant-popover-title {
    padding: 8px 12px 4px 12px !important;
    margin-bottom: 0 !important;
    border-bottom: none !important;
  }

  .ant-popover-inner-content {
    padding-top: 4px !important;
  }
`;

export const CompareReturnModal: React.FC<CompareReturnModalProps> = ({
  visible,
  onClose,
  onSelect,
  vatReturnLogs,
  currentDataLogId,
  connectionId,
  mappingId,
  trigger,
  currentUser,
}) => {
  const { t, i18n } = useTranslation();
  // Filter out the current return and cancelled returns, sort by date (newest first)
  const availableReturns =
    vatReturnLogs
      ?.filter(
        (log) =>
          log.mappingId === mappingId &&
          log.connectionId === connectionId &&
          log.uuid !== currentDataLogId &&
          DataLogStatus[log.status] !== DataLogStatus.CANCELLED
      )
      .sort((a, b) => {
        const dateA = moment(a.toReportingDate);
        const dateB = moment(b.toReportingDate);
        return dateB.diff(dateA);
      }) || [];

  const renderPeriodDates = (_, record: VatReturnLog) => {
    return (
      <Text
        ellipsis={{ tooltip: true }}
        style={{
          fontSize: '12px',
          color: 'var(--color-text-dark, #042a2a)',
          maxWidth: 150,
        }}
      >
        {formatDisplayDateRange(
          record.fromReportingDate,
          record.toReportingDate,
          'DD/MM/YY',
          i18n.language
        )}
      </Text>
    );
  };

  const renderSubmissionDate = (_, record: VatReturnLog) => {
    if (DataLogStatus[record?.status] == DataLogStatus.CANCELLED) {
      return <Text style={{ fontSize: '12px', color: 'var(--color-text-muted, #5a6a6a)' }}>-</Text>;
    } else {
      return (
        <Text style={{ fontSize: '12px', color: 'var(--color-text-muted, #5a6a6a)' }}>
          {record?.submissionDate
            ? formatDisplayDate(
                record?.submissionDate,
                'DD/MM/YYYY',
                i18n.language
              )
            : '-'}
        </Text>
      );
    }
  };

  const renderSortIcon = ({ sortOrder }) => {
    if (sortOrder == 'ascend') {
      return <span style={{ color: 'var(--color-text-muted, #5a6a6a)' }}>↓</span>;
    } else {
      return <span style={{ color: 'var(--color-text-muted, #5a6a6a)' }}>↑</span>;
    }
  };

  const columns = [
    {
      title: t('main.period'),
      dataIndex: 'fromReportingDate',
      key: 'fromReportingDate',
      render: renderPeriodDates,
      sorter: (a, b) => {
        const dateA = moment(a.fromReportingDate);
        const dateB = moment(b.fromReportingDate);
        return dateA.diff(dateB);
      },
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.status'),
      dataIndex: 'status',
      key: 'status',
      render: (content) => (
        <StatusGetter status={DataLogStatus[content]} smaller={true} />
      ),
      sorter: (a, b) => a.status?.localeCompare(b.status),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.submissionDate'),
      dataIndex: 'submissionDate',
      key: 'submissionDate',
      render: renderSubmissionDate,
      sorter: (a, b) => {
        const dateA = a.submissionDate ? moment(a.submissionDate) : moment(0);
        const dateB = b.submissionDate ? moment(b.submissionDate) : moment(0);
        return dateA.diff(dateB);
      },
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.action-required'),
      key: 'action',
      render: (_, record: VatReturnLog) => (
        <ScopeButton
          type="default"
          width="80px"
          height="25px"
          fontSize="12px"
          lineheight="12px"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(record.uuid);
            onClose();
          }}
        >
          {t('vat-report.compare')}
        </ScopeButton>
      ),
    },
  ];

  const content = (
    <PopoverContent>
      {availableReturns.length === 0 ? (
        <Text style={{ color: 'var(--color-text-muted, #5a6a6a)', display: 'block', padding: '16px' }}>
          {t('vat-report.no-previous-returns-available-for-comparison')}
        </Text>
      ) : (
        <div
          style={{ maxHeight: '350px', overflowY: 'auto', overflowX: 'hidden' }}
        >
          <ScopeTable
            columns={columns}
            mainLocaleKey="main"
            originalDataSource={availableReturns}
            tableDataSource={availableReturns}
            pagination={false}
            rowsPerPage={availableReturns.length}
            enableColumnsCustomization={false}
            rowHeight={35}
            fontSize={12}
            showSizeChanger={false}
            getNewPageSize={() => {}}
            transactionType={''}
            reportId={''}
          />
        </div>
      )}
    </PopoverContent>
  );

  const title = (
    <PopoverTitleWrapper>
      <PopoverTitleText>{t('vat-report.select-return-to-compare')}</PopoverTitleText>
      <CloseButton onClick={onClose} aria-label={t('common.close')}>
        <IconClose />
      </CloseButton>
    </PopoverTitleWrapper>
  );

  return (
    <StyledPopover
      content={content}
      title={title}
      trigger="click"
      open={visible}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      placement="bottomRight"
      overlayStyle={{ maxWidth: 750, zIndex: 1000 }}
    >
      {trigger}
    </StyledPopover>
  );
};
